import useTrackTabChange from 'hooks/general/useTrackTabChange';
import { useEffect } from 'react';
import { height } from '../pages/PromoLandscape';
import { G2 } from '@antv/g2plot';
import { isArray } from 'lodash';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import { formatNumWithKMBAlt } from 'utils/helperMethods';

const WaterFallChartIndividualised = ({
    containerId,
    dataSet,
    chartHeight = 400,
    chartWidth = 30,
    xAxisField,
    yAxisField,
    colorField,
    minBound,
    maxBound,
    toolTipLabel,
    tooltipFormatter,
    intervalFormatter,
}: {
    containerId: string;
    dataSet: any[];
    chartHeight: number;
    chartWidth: number;
    xAxisField: string;
    yAxisField: string;
    colorField: string;
    minBound: number;
    maxBound: number;
    toolTipLabel: string;
    tooltipFormatter?: (xAxis: any, val: any) => any;
    intervalFormatter?: (val: number) => string;
}) => {
    const isWindowTabActive = useTrackTabChange();
    useEffect(() => {
        const chart = new G2.Chart({
            container: containerId,
            height: chartHeight,
            width: chartWidth,
            defaultInteractions: ['tooltip'],
        });

        // Ensure data is properly formatted for waterfall chart
        const formattedDataSet = dataSet.map((item) => {
            const yValue = item[yAxisField];
            // If yValue is already an array, use it; otherwise convert to array
            if (isArray(yValue)) {
                return item;
            } else {
                // If it's a single number, convert to array starting from 0
                return {
                    ...item,
                    [yAxisField]: [0, yValue],
                };
            }
        });

        chart.data(formattedDataSet);

        // Calculate the actual min and max from the data to ensure proper scaling
        // This is critical for waterfall charts to render bars at correct positions
        let dataMin = Number.POSITIVE_INFINITY;
        let dataMax = Number.NEGATIVE_INFINITY;
        formattedDataSet.forEach((item) => {
            const yValue = item[yAxisField];
            if (isArray(yValue)) {
                // For arrays, we need both start and end values to properly scale
                dataMin = Math.min(dataMin, yValue[0], yValue[1]);
                dataMax = Math.max(dataMax, yValue[0], yValue[1]);
            } else {
                dataMin = Math.min(dataMin, yValue);
                dataMax = Math.max(dataMax, yValue);
            }
        });

        // Use the calculated min/max from data, but ensure they're within the bounds
        // This ensures each bar's start position is correctly represented
        const finalMin = isFinite(dataMin) ? Math.min(minBound, dataMin) : minBound;
        const finalMax = isFinite(dataMax) ? Math.max(maxBound, dataMax) : maxBound;

        chart.scale(yAxisField, {
            nice: true,
            max: finalMax,
            min: finalMin,
        });
        chart.tooltip({
            shared: true,
            showMarkers: false,
        });
        chart.axis(xAxisField, {
            label: null,
        });
        chart.axis(yAxisField, {
            label: null,
            grid: {
                line: {
                    style: {
                        lineDash: [3, 3],
                    },
                },
            },
        });
        chart.legend(yAxisField, false);
        chart.legend(colorField, false);

        chart
            .interval()
            .position(`${xAxisField}*${yAxisField}`)
            .size(40)
            .color(colorField, (val) => {
                if (val === 'negative') {
                    return '#CA3D3D';
                }
                return '#42A531';
            })
            .tooltip(`${xAxisField}*${yAxisField}`, (xAxis, yAxis) => {
                if (isArray(yAxis)) {
                    return tooltipFormatter
                        ? tooltipFormatter(xAxis, yAxis[1] - yAxis[0])
                        : {
                              name: toolTipLabel,
                              value: formatNumWithKMBAlt(yAxis[1] - yAxis[0]),
                          };
                }

                return tooltipFormatter
                    ? tooltipFormatter(xAxis, yAxis)
                    : {
                          name: toolTipLabel,
                          value: formatNumWithKMBAlt(yAxis),
                      };
            })
            .shape('waterfall')
            .label(`${xAxisField}*${yAxisField}`, (xAxis, yAxis) => {
                // Calculate the value to display
                let displayValue: number;
                if (isArray(yAxis)) {
                    displayValue = yAxis[1] - yAxis[0];
                } else {
                    displayValue = yAxis;
                }

                // Determine position based on value (top for positive, bottom for negative)
                const position = displayValue >= 0 ? 'top' : 'bottom';
                const offsetY = displayValue >= 0 ? -5 : 5;

                return {
                    position: position,
                    offsetY: offsetY,
                    content: intervalFormatter ? intervalFormatter(displayValue) : formatNumWithKMBAlt(displayValue),
                    style: {
                        fontSize: 12,
                        fill: '#333',
                        textAlign: 'center',
                    },
                };
            });

        chart.removeInteraction('continuous-filter');
        chart.interaction('continuous-visible-filter', {
            showEnable: false,
        });

        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [
        isWindowTabActive,
        dataSet,
        chartHeight,
        chartWidth,
        xAxisField,
        yAxisField,
        colorField,
        minBound,
        maxBound,
        intervalFormatter,
        tooltipFormatter,
    ]);
    return <div id={containerId}></div>;
};

export default WaterFallChartIndividualised;
